import React, { useEffect, useMemo, useState } from 'react';
import { tenantApi, TenantAlert } from '../services/api';

interface AlertsProps {
  selectedTenantId: string | null;
}

const Alerts: React.FC<AlertsProps> = ({ selectedTenantId }) => {
  const [alerts, setAlerts] = useState<TenantAlert[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acknowledgingAlertId, setAcknowledgingAlertId] = useState<number | null>(null);
  const [resolvingAlertId, setResolvingAlertId] = useState<number | null>(null);
  const [severityFilter, setSeverityFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [eventTypeFilter, setEventTypeFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [orderId, setOrderId] = useState('');
  const [providerEndpoint, setProviderEndpoint] = useState('https://pay.agentbar.ai/webhooks/midtrans');
  const [errorDetail, setErrorDetail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (selectedTenantId) {
      fetchAlerts();
    }
  }, [selectedTenantId]);

  const fetchAlerts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await tenantApi.getAlerts(selectedTenantId!, 50);
      setAlerts(response.alerts);
    } catch (err) {
      console.error('Failed to fetch alerts:', err);
      setError('Gagal memuat alert. Silakan coba lagi nanti.');
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (alertId: number) => {
    setAcknowledgingAlertId(alertId);
    try {
      const response = await tenantApi.acknowledgeAlert(alertId);
      setAlerts((current) =>
        current.map((alert) => (alert.id === alertId ? response.alert : alert))
      );
    } catch (err) {
      console.error('Failed to acknowledge alert:', err);
    } finally {
      setAcknowledgingAlertId(null);
    }
  };

  const handleResolve = async (alertId: number) => {
    setResolvingAlertId(alertId);
    try {
      const response = await tenantApi.resolveAlert(alertId);
      setAlerts((current) =>
        current.map((alert) => (alert.id === alertId ? response.alert : alert))
      );
    } catch (err) {
      console.error('Failed to resolve alert:', err);
    } finally {
      setResolvingAlertId(null);
    }
  };

  const handleReportWebhookFailure = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedTenantId) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await tenantApi.reportWebhookFailure({
        tenantId: selectedTenantId,
        orderId: orderId.trim() || undefined,
        providerEndpoint: providerEndpoint.trim(),
        errorDetail: errorDetail.trim() || 'Webhook delivery failure reported manually by operator',
      });

      setOrderId('');
      setErrorDetail('');
      await fetchAlerts();
    } catch (err) {
      console.error('Failed to report webhook failure:', err);
      setError('Gagal membuat alert webhook failure.');
    } finally {
      setSubmitting(false);
    }
  };

  const uniqueEventTypes = useMemo(
    () => Array.from(new Set(alerts.map((alert) => alert.eventType))).sort(),
    [alerts]
  );

  const filteredAlerts = alerts.filter((alert) => {
    const severityMatches = severityFilter === 'ALL' || alert.severity === severityFilter;
    const statusMatches = statusFilter === 'ALL' || alert.status === statusFilter;
    const eventTypeMatches = eventTypeFilter === 'ALL' || alert.eventType === eventTypeFilter;
    const normalizedSearch = searchQuery.trim().toLowerCase();
    const searchMatches =
      normalizedSearch.length === 0 ||
      alert.title.toLowerCase().includes(normalizedSearch) ||
      alert.message.toLowerCase().includes(normalizedSearch) ||
      alert.eventType.toLowerCase().includes(normalizedSearch) ||
      alert.status.toLowerCase().includes(normalizedSearch);
    return severityMatches && statusMatches && eventTypeMatches && searchMatches;
  });

  if (!selectedTenantId) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Silakan pilih tenant untuk melihat alert</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Tenant Alerts</h2>
          <p className="text-sm text-gray-500">Kelola alert operasional dan laporkan webhook failure manual.</p>
        </div>
        <button
          type="button"
          onClick={fetchAlerts}
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
        >
          Refresh
        </button>
      </div>

      <form onSubmit={handleReportWebhookFailure} className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h3 className="text-lg font-semibold">Report Webhook Failure</h3>
        <p className="mt-1 text-sm text-gray-500">Gunakan ini jika Midtrans/provider melaporkan webhook gagal terkirim.</p>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <label className="flex flex-col gap-1 text-sm text-gray-600">
            Order ID
            <input
              type="text"
              value={orderId}
              onChange={(event) => setOrderId(event.target.value)}
              placeholder="PREMIUMWP-..."
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-gray-600">
            Provider Endpoint
            <input
              type="text"
              value={providerEndpoint}
              onChange={(event) => setProviderEndpoint(event.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700"
            />
          </label>
        </div>
        <label className="mt-4 flex flex-col gap-1 text-sm text-gray-600">
          Error Detail
          <textarea
            value={errorDetail}
            onChange={(event) => setErrorDetail(event.target.value)}
            rows={4}
            placeholder="MethodNotAllowed, timeout, invalid endpoint, dll."
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700"
          />
        </label>
        <div className="mt-4">
          <button
            type="submit"
            disabled={submitting}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? 'Saving...' : 'Create Webhook Failure Alert'}
          </button>
        </div>
      </form>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <h3 className="text-lg font-semibold text-red-900">Error</h3>
          <p className="text-red-700">{error}</p>
        </div>
      )}

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="text-lg font-semibold">Alerts</h3>
            <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700">
              {filteredAlerts.length} shown
            </span>
          </div>
          <div className="mt-4 flex flex-col gap-3 md:flex-row">
            <label className="flex flex-col gap-1 text-sm text-gray-600 md:min-w-64">
              Search
              <input
                type="text"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Cari title, message, event type, status..."
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-gray-600">
              Severity
              <select
                value={severityFilter}
                onChange={(event) => setSeverityFilter(event.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700"
              >
                <option value="ALL">All severities</option>
                <option value="CRITICAL">Critical</option>
                <option value="HIGH">High</option>
                <option value="WARN">Warn</option>
                <option value="INFO">Info</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm text-gray-600">
              Event Type
              <select
                value={eventTypeFilter}
                onChange={(event) => setEventTypeFilter(event.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700"
              >
                <option value="ALL">All event types</option>
                {uniqueEventTypes.map((eventType) => (
                  <option key={eventType} value={eventType}>
                    {eventType}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm text-gray-600">
              Status
              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700"
              >
                <option value="ALL">All statuses</option>
                <option value="PENDING">Pending</option>
                <option value="ACKNOWLEDGED">Acknowledged</option>
                <option value="RESOLVED">Resolved</option>
              </select>
            </label>
          </div>
        </div>
        <div className="divide-y divide-gray-100">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="spinner"></div>
            </div>
          ) : filteredAlerts.length === 0 ? (
            <div className="px-6 py-5 text-sm text-gray-500">Belum ada alert operasional.</div>
          ) : (
            filteredAlerts.map((alert) => (
              <div key={alert.id} className="px-6 py-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="font-medium text-gray-900">{alert.title}</div>
                    <div className="text-sm text-gray-600">{alert.message}</div>
                    <div className="mt-1 text-xs text-gray-500">
                      {alert.eventType} · {new Date(alert.createdAt).toLocaleString('id-ID')} · {alert.status}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                      alert.severity === 'HIGH' || alert.severity === 'CRITICAL'
                        ? 'bg-red-100 text-red-700'
                        : alert.severity === 'WARN'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-blue-100 text-blue-700'
                    }`}>
                      {alert.severity}
                    </span>
                    <div className="flex items-center gap-2">
                      {alert.status === 'PENDING' && (
                        <button
                          type="button"
                          onClick={() => handleAcknowledge(alert.id)}
                          disabled={acknowledgingAlertId === alert.id}
                          className="rounded-lg border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {acknowledgingAlertId === alert.id ? 'Saving...' : 'Acknowledge'}
                        </button>
                      )}
                      {alert.status !== 'RESOLVED' && (
                        <button
                          type="button"
                          onClick={() => handleResolve(alert.id)}
                          disabled={resolvingAlertId === alert.id}
                          className="rounded-lg bg-green-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {resolvingAlertId === alert.id ? 'Saving...' : 'Resolve'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Alerts;
